import {AnimatePresence, m} from 'framer-motion';
import {opacityAnimation} from '@common/ui/animation/opacity-animation';
import {ReactNode, useEffect, useRef} from 'react';
import {useTwoFactorQrCode} from '@common/auth/ui/two-factor/requests/use-two-factor-qr-code';
import {useForm} from 'react-hook-form';
import {
  ConfirmTwoFactorPayload,
  useConfirmTwoFactor,
} from '@common/auth/ui/two-factor/requests/use-confirm-two-factor';
import {TwoFactorStepperLayout} from '@common/auth/ui/two-factor/stepper/two-factor-stepper-layout';
import {Trans} from '@common/i18n/trans';
import {Skeleton} from '@common/ui/skeleton/skeleton';
import {Form} from '@common/ui/forms/form';
import {queryClient} from '@common/http/query-client';
import {FormTextField} from '@common/ui/forms/input-field/text-field/text-field';
import {useDisableTwoFactor} from '@common/auth/ui/two-factor/requests/use-disable-two-factor';
import {usePasswordConfirmedAction} from '@common/auth/ui/confirm-password/use-password-confirmed-action';
import {Button} from '@common/ui/buttons/button';
import axios from 'axios';

interface Props {
  onCancel: () => void;
  onConfirmed: () => void;
}
export function TwoFactorConfirmationStep(props: Props) {
  const {data, error, refetch} = useTwoFactorQrCode();
  const retriedAfterConfirmRef = useRef(false);
  const {withConfirmedPassword} = usePasswordConfirmedAction({needsPassword: true});

  useEffect(() => {
    if (!axios.isAxiosError(error) || error.response?.status !== 423) return;
    if (retriedAfterConfirmRef.current) return;
    retriedAfterConfirmRef.current = true;
    withConfirmedPassword(() => {
      refetch();
    });
  }, [error, refetch, withConfirmedPassword]);

  return (
    <TwoFactorStepperLayout
      title={<Trans message="Finish enabling two factor authentication." />}
      description={
        <Trans message="To finish enabling two factor authentication, scan the following QR code using your phone's authenticator application or enter the setup key and provide the generated OTP code." />
      }
    >
      <AnimatePresence initial={false}>
        {!data ? (
          <QrCodeLayout
            animationKey="svg-skeleton"
            svg={<Skeleton variant="rect" size="w-full h-full" />}
            secret={<Skeleton variant="text" className="max-w-224" />}
          />
        ) : (
          <QrCodeLayout
            animationKey="real-svg"
            svg={<div dangerouslySetInnerHTML={{__html: data.svg}} />}
            secret={
              <Trans message="Setup key: :key" values={{key: data.secret}} />
            }
          />
        )}
      </AnimatePresence>
      <CodeForm {...props} />
    </TwoFactorStepperLayout>
  );
}

function CodeForm({onCancel, onConfirmed}: Props) {
  const form = useForm<ConfirmTwoFactorPayload>();
  const confirmTwoFactor = useConfirmTwoFactor(form);
  const disableTwoFactor = useDisableTwoFactor();
  const {withConfirmedPassword, isLoading: confirmPasswordIsLoading} =
    usePasswordConfirmedAction();
  const isLoading =
    confirmTwoFactor.isLoading ||
    disableTwoFactor.isLoading ||
    confirmPasswordIsLoading;

  return (
    <Form
      form={form}
      onSubmit={values =>
        withConfirmedPassword(() => {
          confirmTwoFactor.mutate(values, {
            onSuccess: () => {
              queryClient.invalidateQueries(['users']);
              onConfirmed();
            },
          });
        })
      }
    >
      <FormTextField
        required
        name="code"
        label={<Trans message="Code" />}
        autoFocus
      />
      <div className="mt-24 flex items-center justify-center gap-12">
        <Button
          type="button"
          variant="flat"
          color="primary"
          className="h-[52px] rounded-[20px] border border-[#2B7E20] bg-[#2B7E20] px-24 text-[14px] font-medium text-white disabled:opacity-70 dark:border-[#2B7E20] dark:bg-[#2B7E20]"
          disabled={isLoading}
          onClick={() => {
            withConfirmedPassword(() => {
              disableTwoFactor.mutate(undefined, {onSuccess: onCancel});
            });
          }}
        >
          <Trans message="Cancel" />
        </Button>
        <Button
          type="submit"
          variant="flat"
          color="primary"
          className="h-[52px] rounded-[20px] border border-[#2B7E20] bg-[#2B7E20] px-24 text-[14px] font-medium text-white disabled:opacity-70 dark:border-[#2B7E20] dark:bg-[#2B7E20]"
          disabled={isLoading}
        >
          <Trans message="Confirm" />
        </Button>
      </div>
    </Form>
  );
}

interface QrCodeLayoutProps {
  animationKey: string;
  svg: ReactNode;
  secret: ReactNode;
}
function QrCodeLayout({animationKey, svg, secret}: QrCodeLayoutProps) {
  return (
    <m.div key={animationKey} {...opacityAnimation}>
      <div className="w-192 h-192 mb-16">{svg}</div>
      <div className="text-sm font-medium mb-16">{secret}</div>
    </m.div>
  );
}
